import { lazy } from "react";

// A tab that was open before a deploy still holds an index.html that points at
// chunk hashes the server no longer serves. The next lazily loaded view then
// fails with a ChunkLoadError, React throws it during render, and the
// LayoutPage ErrorBoundary shows the hippo page — reproduced on the generative
// workspace, whose five views are all lazy chunks. A full reload fetches the
// current index.html and fixes it, which is what the hippo page asks the user
// to do by hand. This does it for them: retry the import once (transient
// network hiccup), then reload once per URL (stale deploy), and only if the
// chunk still fails after that let the error through to the boundary.

export const CHUNK_RELOAD_KEY = "chunk-reload-attempted";
// One reload per URL within this window. A marker is never cleared on a
// successful import: the app shell always loads after the reload, and clearing
// there would re-arm the reload for a chunk that is genuinely missing — an
// endless reload loop. A later deploy, hours later, gets its own reload.
export const CHUNK_RELOAD_TTL_MS = 60 * 1000;

export const isChunkLoadError = (error) =>
  !!error &&
  (error.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(String(error.message || error)) ||
    /Failed to fetch dynamically imported module/i.test(String(error.message || error)));

const storage = () => {
  try {
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
};

// One reload per page URL per TTL: a second failure on the same URL within the
// window means the chunk is really gone (or the network is down), and
// reloading again would only loop — the error then reaches the boundary.
export const reloadOnceForStaleChunk = (locationLike = window.location, now = Date.now()) => {
  const key = `${CHUNK_RELOAD_KEY}:${locationLike.pathname}${locationLike.search}`;
  const store = storage();
  const last = Number((store && store.getItem(key)) || 0);
  if (last && now - last < CHUNK_RELOAD_TTL_MS) return false;
  try {
    if (store) store.setItem(key, String(now));
  } catch (e) {
    // quota / private mode: reload anyway, the TTL guard is best effort
  }
  locationLike.reload();
  return true;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const importWithRetry = async (importer, { retryDelayMs = 400 } = {}) => {
  try {
    return await importer();
  } catch (firstError) {
    if (!isChunkLoadError(firstError)) throw firstError;
    await wait(retryDelayMs);
    try {
      return await importer();
    } catch (secondError) {
      if (!isChunkLoadError(secondError)) throw secondError;
      if (reloadOnceForStaleChunk()) {
        // The reload is under way; never resolve so nothing renders meanwhile.
        return new Promise(() => {});
      }
      throw secondError;
    }
  }
};

const lazyWithRetry = (importer, options) => lazy(() => importWithRetry(importer, options));

export default lazyWithRetry;
